import { Translation } from "../model/Language.js";
import { BaseElementOptions, BaseElement } from "./BaseElement.js";

export interface MenuOptions extends BaseElementOptions {
    element?: HTMLDivElement;
    disabled?: boolean;
}

export interface MenuButtonOptions {
    cssClass?: string;

    label?: Translation;
    labelActive?: Translation;
    labelDisabled?: Translation;

    icon?: string;
    iconActive?: string;
    iconDisabled?: string;

    ariaLabel?: Translation;
    ariaLabelActive?: Translation;
    ariaLabelDisabled?: Translation;

    showTooltip?: boolean;
    position?: "start" | "center" | "end";
    tagName?: keyof HTMLElementTagNameMap;
    disabled?: boolean;
    active?: boolean;
    visible?: boolean;
}

export class Menu extends BaseElement {

    public create(options: MenuOptions): this {

        var element: HTMLDivElement = null;
        if (options.element) {
            element = options.element;
        } else {
            element = document.createElement("div");
            element.innerHTML = `
            <div class="awi-menu__button-list awi-menu__button-list--start"></div>
            <div class="awi-menu__button-list awi-menu__button-list--center"></div>
            <div class="awi-menu__button-list awi-menu__button-list--end"></div>
            `
        }

        element.classList.add("awi-menu");

        this.setElement(element);
        super.create(options);

        if (options.disabled) {
            this.disabled = true;
        }

        return this;
    }

    public addButton(options: MenuButtonOptions): MenuButton {
        var button = new MenuButton(options);
        var position = options.position;
        if (!position) {
            position = "start";
        }
        this.element.querySelector(".awi-menu__button-list--" + position).appendChild(button.element);
        return button;
    }

    public get disabled(): boolean {
        return this.element.getAttribute("aria-disabled") === "true";
    }

    public set disabled(value: boolean) {
        this.element.setAttribute("aria-disabled", value ? "true" : "false");
    }
}

export class MenuButton {
    public readonly element: HTMLElement;

    private _icon: string = null;
    private _iconDefault: string = null;
    private _iconActive: string = null;
    private _iconDisabled: string = null;

    private _label: Translation = null;
    private _labelDefault: Translation = null;
    private _labelActive: Translation = null;
    private _labelDisabled: Translation = null;

    private _ariaLabel: Translation = null;
    private _ariaLabelDefault: Translation = null;
    private _ariaLabelActive: Translation = null;
    private _ariaLabelDisabled: Translation = null;

    private _showTooltip: boolean = false;

    constructor(options: MenuButtonOptions) {
        options = e5.object.merge({
            position: "start",
            tagName: "button",
            disabled: false,
            active: false,
            showTooltip: false
        } as MenuButtonOptions, options);

        this._showTooltip = options.showTooltip;

        this.element = document.createElement(options.tagName);
        this.element.className = "awi-menu__button";
        if (options.cssClass) {
            this.element.classList.add(options.cssClass);
        }

        this._labelDefault = options.label;
        this._labelActive = options.labelActive;
        this._labelDisabled = options.labelDisabled;

        this._ariaLabelDefault = options.ariaLabel;
        this._ariaLabelActive = options.ariaLabelActive;
        this._ariaLabelDisabled = options.ariaLabelDisabled;
        
        this._iconDefault = options.icon;
        this._iconActive = options.iconActive;
        this._iconDisabled = options.iconDisabled;

        if (options.disabled) {
            this.element.setAttribute("aria-disabled", "true");
        }

        if (options.active) {
            this.element.classList.add("awi-menu__button--active");
        }

        if (options.visible === false) {
            this.visible = false;
        }

        this.update();
    }

    public get icon(): string {
        return this._icon;
    }

    public set icon(icon: string) {
        if (this._icon === icon) {
            return;
        }
        this._icon = icon;

        var i = this.element.querySelector<HTMLSpanElement>(".awi-menu__button-icon");
        if (i) {
            i.remove();
        }

        if (icon) {
            i = document.createElement("span");
            i.className = "awi-menu__button-icon " + icon;
            this.element.prepend(i);
        }
    }

    public get ariaLabel(): Translation {
        return this._ariaLabel;
    }

    public set ariaLabel(value: Translation) {
        this._ariaLabel = value;
        if (lang.isEmpty(value)) {
            lang.clear(this.element);
        } else {
            var type = "@aria-label";
            if (this._showTooltip) {
                type += "|@data-tooltip";
            }
            lang.setType(this.element, type, value);
        }
    }

    public get label(): Translation {
        return this._label;
    }

    public set label(label: Translation) {
        this._label = label;

        var lab = this.element.querySelector<HTMLSpanElement>(".awi-menu__button-label");
        if (lab) {
            lab.remove();
        }

        if (!lang.isEmpty(label)) {
            lab = document.createElement("span");
            lab.className = "awi-menu__button-label";
            lang.set(lab, label);
            this.element.appendChild(lab);
        }
    }

    public get disabled(): boolean {
        return this.element.getAttribute("aria-disabled") === "true";
    }

    public set disabled(value: boolean) {
        if (this.disabled === value) {
            return;
        }
        this.element.setAttribute("aria-disabled", value ? "true" : "false");
        this.update();
    }

    public get active(): boolean {
        return this.element.classList.contains("awi-menu__button--active");
    }

    public set active(value: boolean) {
        if (value === this.active) {
            return;
        }
        this.element.classList.toggle("awi-menu__button--active", value);
        this.update();
    }

    public get visible(): boolean {
        return !this.element.classList.contains("e5-hidden");
    }

    public set visible(value: boolean) {
        this.element.classList.toggle("e5-hidden", !value);
    }

    private update(): void {
        if (this.disabled && this._ariaLabelDisabled) {
            this.ariaLabel = this._ariaLabelDisabled;
        } else if (this.active && this._ariaLabelActive) {
            this.ariaLabel = this._ariaLabelActive;
        } else if(this._ariaLabelDefault){
            this.ariaLabel = this._ariaLabelDefault;
        }

        if (this.disabled && this._labelDisabled) {
            this.label = this._labelDisabled;
        } else if (this.active && this._labelActive) {
            this.label = this._labelActive;
        } else if(this._labelDefault){
            this.label = this._labelDefault;
        }

        if (this.disabled && this._iconDisabled) {
            this.icon = this._iconDisabled;
        } else if (this.active && this._iconActive) {
            this.icon = this._iconActive;
        } else if(this._iconDefault){
            this.icon = this._iconDefault;
        }
    }

    public remove():void{
        this.element.remove();
    }
}